import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  ICartCountResponse,
  ICartItemDetail,
  ICartResponse,
} from '@/common/interfaces/cart.interface';
import type { IStoreCartDesignGroupSelection } from '@/modules/Cart/StoreCart/helpers/storeCartOptionSelection.utils';
import { areStoreCartDesignGroupSelectionsEqual } from '@/modules/Cart/StoreCart/helpers/storeCartOptionSelection.utils';
import {
  DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS,
  type IStoreCartDesignGroupApiStatus,
  type StoreCartDesignGroupApiStatus,
} from '@/modules/Cart/StoreCart/storeCart.constants';
import {
  mergeStoreCartDesignGroupShellsState,
  reindexStoreCartDesignGroupShellNames,
  areStoreCartDesignGroupShellsEqual,
  type IStoreCartDesignGroupShell,
} from '@/modules/Cart/StoreCart/helpers/storeCartDesignGroups.utils';

export type { IStoreCartDesignGroupSelection };
export type { IStoreCartDesignGroupShell };
export type { IStoreCartDesignGroupApiStatus };
export type { StoreCartDesignGroupApiStatus };

interface StoreCartState {
  storeItems: ICartResponse;
  storeCountObject: ICartCountResponse;
  showAddStoreSuccessMessage: boolean;
  isLoading: boolean;
  isOpened: boolean;
  optionSelectionsByGroupKey: Record<string, IStoreCartDesignGroupSelection>;
  designGroupShellsByKey: Record<string, IStoreCartDesignGroupShell>;
  designGroupShellOrder: number;
  designGroupMoqByKey: Record<string, number>;
  isDesignGroupMoqLoading: boolean;
  designGroupMoqFetchRequestKey: string;
  designGroupApiStatusByKey: Record<string, IStoreCartDesignGroupApiStatus>;
}

const initialState: StoreCartState = {
  storeItems: [] as ICartResponse,
  storeCountObject: {} as ICartCountResponse,
  showAddStoreSuccessMessage: false,
  isLoading: false,
  isOpened: false,
  optionSelectionsByGroupKey: {},
  designGroupShellsByKey: {},
  designGroupShellOrder: 0,
  designGroupMoqByKey: {},
  isDesignGroupMoqLoading: false,
  designGroupMoqFetchRequestKey: '',
  designGroupApiStatusByKey: {},
};

export const storeCartSlice = createSlice({
  name: 'storeCart',
  initialState,
  reducers: {
    setStoreItems: (state, action: PayloadAction<ICartResponse>) => {
      state.storeItems = action.payload;
    },
    setStoreCountObject: (state, action: PayloadAction<ICartCountResponse>) => {
      state.storeCountObject = action.payload;
    },
    updateStoreCountObject: (state, action: PayloadAction<Partial<ICartCountResponse>>) => {
      state.storeCountObject = {
        ...state.storeCountObject,
        ...action.payload,
      };
    },
    enableAddStoreSuccessMessage: (state) => {
      state.showAddStoreSuccessMessage = true;
    },
    disableAddStoreSuccessMessage: (state) => {
      state.showAddStoreSuccessMessage = false;
    },
    setStoreLoadingTrue: (state) => {
      state.isLoading = true;
    },
    setStoreLoadingFalse: (state) => {
      state.isLoading = false;
    },
    setStoreCartOpened: (state, action: PayloadAction<boolean>) => {
      state.isOpened = action.payload;
    },
    setStoreCartDesignGroupSelection: (
      state,
      action: PayloadAction<{
        groupKey: string;
        selection: IStoreCartDesignGroupSelection;
      }>
    ) => {
      const { groupKey, selection } = action.payload;
      const currentSelection = state.optionSelectionsByGroupKey[groupKey];

      if (areStoreCartDesignGroupSelectionsEqual(currentSelection, selection)) {
        return;
      }

      state.optionSelectionsByGroupKey[groupKey] = selection;
    },
    initializeStoreCartOptionSelections: (
      state,
      action: PayloadAction<Record<string, IStoreCartDesignGroupSelection>>
    ) => {
      Object.entries(action.payload).forEach(([groupKey, selection]) => {
        if (!state.optionSelectionsByGroupKey[groupKey]) {
          state.optionSelectionsByGroupKey[groupKey] = selection;
        }
      });
    },
    syncStoreCartOptionSelectionsFromCartItems: (
      state,
      action: PayloadAction<Record<string, IStoreCartDesignGroupSelection>>
    ) => {
      Object.entries(action.payload).forEach(([groupKey, selection]) => {
        state.optionSelectionsByGroupKey[groupKey] = selection;
      });
    },
    clearStoreCartOptionSelections: (state) => {
      state.optionSelectionsByGroupKey = {};
    },
    removeStoreCartDesignGroupSelection: (state, action: PayloadAction<string>) => {
      delete state.optionSelectionsByGroupKey[action.payload];
    },
    setStoreCartDesignGroupMoqs: (state, action: PayloadAction<Record<string, number>>) => {
      state.designGroupMoqByKey = action.payload;
    },
    clearStoreCartDesignGroupMoqs: (state) => {
      state.designGroupMoqByKey = {};
      state.isDesignGroupMoqLoading = false;
      state.designGroupMoqFetchRequestKey = '';
    },
    setDesignGroupMoqFetchLoading: (state, action: PayloadAction<string>) => {
      state.isDesignGroupMoqLoading = true;
      state.designGroupMoqFetchRequestKey = action.payload;
    },
    setDesignGroupMoqFetchComplete: (state, action: PayloadAction<string>) => {
      state.isDesignGroupMoqLoading = false;
      state.designGroupMoqFetchRequestKey = action.payload;
    },
    clearDesignGroupMoqFetchState: (state) => {
      state.isDesignGroupMoqLoading = false;
      state.designGroupMoqFetchRequestKey = '';
    },
    setDesignGroupQuoteStatus: (
      state,
      action: PayloadAction<{ groupKey: string; status: StoreCartDesignGroupApiStatus }>
    ) => {
      const current =
        state.designGroupApiStatusByKey[action.payload.groupKey] ??
        DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS;

      state.designGroupApiStatusByKey[action.payload.groupKey] = {
        ...current,
        quote: action.payload.status,
      };
    },
    setDesignGroupMoqStatus: (
      state,
      action: PayloadAction<{ groupKey: string; status: StoreCartDesignGroupApiStatus }>
    ) => {
      const current =
        state.designGroupApiStatusByKey[action.payload.groupKey] ??
        DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS;

      state.designGroupApiStatusByKey[action.payload.groupKey] = {
        ...current,
        moq: action.payload.status,
      };
    },
    setDesignGroupMoqStatuses: (
      state,
      action: PayloadAction<Record<string, StoreCartDesignGroupApiStatus>>
    ) => {
      Object.entries(action.payload).forEach(([groupKey, status]) => {
        const current =
          state.designGroupApiStatusByKey[groupKey] ?? DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS;

        state.designGroupApiStatusByKey[groupKey] = {
          ...current,
          moq: status,
        };
      });
    },
    clearDesignGroupApiStatuses: (state) => {
      state.designGroupApiStatusByKey = {};
    },
    removeDesignGroupApiStatus: (state, action: PayloadAction<string>) => {
      delete state.designGroupApiStatusByKey[action.payload];
    },
    mergeStoreCartDesignGroupShellsFromCartItems: (
      state,
      action: PayloadAction<ICartItemDetail[]>
    ) => {
      if (!action.payload.length) {
        return;
      }

      const { shells, nextOrder } = mergeStoreCartDesignGroupShellsState(
        state.designGroupShellsByKey,
        action.payload,
        state.designGroupShellOrder
      );

      if (
        nextOrder === state.designGroupShellOrder &&
        areStoreCartDesignGroupShellsEqual(state.designGroupShellsByKey, shells)
      ) {
        return;
      }

      state.designGroupShellsByKey = shells;
      state.designGroupShellOrder = nextOrder;
    },
    removeStoreCartDesignGroupShell: (state, action: PayloadAction<string>) => {
      const nextShells = { ...state.designGroupShellsByKey };
      delete nextShells[action.payload];
      delete state.designGroupMoqByKey[action.payload];
      delete state.designGroupApiStatusByKey[action.payload];
      state.designGroupShellsByKey = reindexStoreCartDesignGroupShellNames(nextShells);
    },
    resetStoreCartState: (state) => {
      state.storeItems = [];
      state.optionSelectionsByGroupKey = {};
      state.designGroupShellsByKey = {};
      state.designGroupShellOrder = 0;
      state.designGroupMoqByKey = {};
      state.isDesignGroupMoqLoading = false;
      state.designGroupMoqFetchRequestKey = '';
      state.designGroupApiStatusByKey = {};
      state.isOpened = false;
    },
  },
});

export const {
  setStoreItems,
  setStoreCountObject,
  updateStoreCountObject,
  enableAddStoreSuccessMessage,
  disableAddStoreSuccessMessage,
  setStoreLoadingTrue,
  setStoreLoadingFalse,
  setStoreCartOpened,
  setStoreCartDesignGroupSelection,
  initializeStoreCartOptionSelections,
  syncStoreCartOptionSelectionsFromCartItems,
  clearStoreCartOptionSelections,
  removeStoreCartDesignGroupSelection,
  setStoreCartDesignGroupMoqs,
  clearStoreCartDesignGroupMoqs,
  setDesignGroupMoqFetchLoading,
  setDesignGroupMoqFetchComplete,
  clearDesignGroupMoqFetchState,
  setDesignGroupQuoteStatus,
  setDesignGroupMoqStatus,
  setDesignGroupMoqStatuses,
  clearDesignGroupApiStatuses,
  removeDesignGroupApiStatus,
  mergeStoreCartDesignGroupShellsFromCartItems,
  removeStoreCartDesignGroupShell,
  resetStoreCartState,
} = storeCartSlice.actions;

export default storeCartSlice.reducer;
